import {
  Grid,
  Paper,
  Typography,
  Button,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { Helmet } from "react-helmet";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import { useForm } from "react-hook-form";
import {
  callAPI,
  clearRequest,
  customValidation,
  Method,
  validations
} from "../../commonFunctions/CommonFunctions";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";

export default function MyAccount() {
  const { register, handleSubmit, control, watch, setValue } = useForm();
  const { id } = useParams();
  const { showMessage } = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const [updatePsw, setUpdatePsw] = useState<boolean>(false);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const response: any = await callAPI(
          "auth/account/getBy",
          Method.GET,
          {},
          id
        );

        console.log("response user", response);
        const data = response?.user;

        setValue("fname", data.fname);
        setValue("lname", data.lname);
        setValue("email", data.email);
        setValue("phoneNumber", data.phoneNumber);
        setValue("pinCode", data.pinCode);
        setValue("jobTitle", data.jobTitle);
        setValue("userType", data.userType);
      } catch (error: any) {
        showMessage(
          error.response?.data?.message || "Failed to retrieve account data",
          "error"
        );
        console.error("Failed to retrieve account data", error);
      }
    };

    fetchAccount();
  }, [id, setValue]);

  const handleData = async (data: any) => {
    if (!updatePsw) {
      delete data.password;
      delete data.confirmPassword;
    }

    try {
      const request = {
        fname: data.fname,
        lname: data.lname,
        email: data.email,
        phoneNumber: data.phoneNumber,
        pinCode: data.pinCode,
        jobTitle: data.jobTitle,
        ...(updatePsw && { password: data.password }) // only send if checkbox checked true;
      };

      const newRequest = clearRequest(request);
      setLoading(true);

      const response: any = await callAPI(
        `auth/account/update/${id}`,
        Method.PUT,
        newRequest
      );

      if (response && response.success) {
        showMessage(response.message || "Account updated successfully!", "success");
      }
    } catch (e: any) {
      if (e.message === 'GlobalHandledError') return;
      showMessage(e.response?.data?.message || "An unexpected error occurred.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Account Page | Willsoar</title>
      </Helmet>
      <Grid
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: "100vh", backgroundColor: "#eaf0f6", p: 2 }}
      >
        <Grid item xs={12} sm={10} md={8} lg={6}>
          <Typography
            variant="h1"
            fontSize={28}
            fontWeight={600}
            align="left"
            gutterBottom
          >
            My Account
          </Typography>

          <Paper elevation={1} sx={{ p: 4, borderRadius: 2, width: "70%" }}>
            <Grid container spacing={2}>
              <form
                onSubmit={handleSubmit(handleData)}
                style={{ width: "100%" }}
              >
                {/* First and Last Name */}
                <Grid item xs={12} sm={6}>
                  <WSTextField
                    control={control}
                    id="fname"
                    {...register("fname", {
                      required: "First Name is required"
                    })}
                    name="fname"
                    rules={{ required: true }}
                    label="First Name *"
                    labelType="Normal"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <WSTextField
                    control={control}
                    id="lname"
                    {...register("lname")}
                    name="lname"
                    rules={{ required: false }}
                    label="Last Name "
                    labelType="Normal"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>

                {/* Job Title */}
                <Grid item xs={12}>
                  <WSTextField
                    id="jobTitle"
                    {...register("jobTitle", {
                      required: "Job Title is required"
                    })}
                    rules={{ required: true }}
                    label="Job Title"
                    name="jobTitle"
                    control={control}
                    labelType="Normal"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <WSTextField
                    id="email"
                    {...register("email", { required: "Email ID is required" })}
                    name="email"
                    rules={customValidation(true, validations.email)}
                    label="Email Address *"
                    control={control}
                    labelType="Normal"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>

                {/* Pin Code */}
                <Grid item xs={12}>
                  <WSTextField
                    control={control}
                    id="pinCode"
                    {...register("pinCode", {
                      required: "PinCode is required"
                    })}
                    rules={{ required: true }}
                    minLength={6}
                    maxLength={6}
                    type={TextType.AllowOnlyNumber}
                    name="pinCode"
                    label="PINCODE"
                    labelType="Normal"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12}>
                  <WSTextField
                    control={control}
                    id="phoneNumber"
                    {...register("phoneNumber", {
                      required: "Phone Number is required"
                    })}
                    name="phoneNumber"
                    rules={{
                      required: true
                    }}
                    type={TextType.Number}
                    label="Phone Number"
                    labelType="Normal"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updatePsw}
                        onChange={(e: any) => setUpdatePsw(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Update Password"
                  />
                </Grid>

                <Grid item xs={12}>
                  <WSTextField
                    control={control}
                    id="userType"
                    name="userType"
                    label="User-Type"
                    labelType="Normal"
                    sx={{
                      marginBottom: 1,
                      backgroundColor: "rgba(0,0,0,.05)",
                      boxShadow: "none",
                      border: 0,
                      color: "#444",
                      fontSize: 16,
                      transition: "width 0.25s"
                    }}
                    disabled
                  />
                </Grid>

                {updatePsw && (
                  <>
                    {/* New Password */}
                    <Grid item xs={12}>
                      <WSTextField
                        control={control}
                        id="password"
                        {...register("password", {
                          required: "Password is required",
                          validate: (value: any) => {
                            const confirmPassword = watch("confirmPassword");
                            return (
                              value === confirmPassword ||
                              "Passwords do not match"
                            );
                          }
                        })}
                        name="password"
                        label="New Password *"
                        type={TextType.Password}
                        labelType="Normal"
                        sx={{
                          marginBottom: 1,
                          backgroundColor: "rgba(0,0,0,.05)",
                          boxShadow: "none",
                          border: 0,
                          color: "#444",
                          fontSize: 16,
                          transition: "width 0.25s"
                        }}
                      />
                    </Grid>

                    {/* Confirm Password */}
                    <Grid item xs={12}>
                      <WSTextField
                        control={control}
                        id="confirmPassword"
                        {...register("confirmPassword", {
                          required: "Confirm Password is required",
                          validate: (value: any) => {
                            const password = watch("password");
                            return (
                              value === password || "Passwords do not match"
                            );
                          }
                        })}
                        name="confirmPassword"
                        label="Confirm New Password *"
                        type={TextType.Password}
                        labelType="Normal"
                        sx={{
                          marginBottom: 1,
                          backgroundColor: "rgba(0,0,0,.05)",
                          boxShadow: "none",
                          border: 0,
                          color: "#444",
                          fontSize: 16,
                          transition: "width 0.25s"
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Save Button */}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="warning"
                    type="submit"
                    sx={{ backgroundColor: "#fe6a2c", textTransform: "none" }}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Account"}
                  </Button>
                </Grid>
              </form>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
